// Pricing.tsx — public /pricing route. Anonymous-accessible.
// Customer-facing voice (per .github/instructions/marketing.instructions.md):
// no internal jargon; lead with the value to the contractor, not the architecture.
// CTAs route to Stripe Checkout for the chosen SKU (handled inside the auth flow).

import { useState } from 'react';
import { useAuth } from '../auth/AuthProvider';
import SignInPanel from '../auth/SignInPanel';
import { startCheckout, type CheckoutSku } from '../utils/checkout';

interface Card {
  sku: CheckoutSku;
  title: string;
  price: string;
  sub: string;
  features: string[];
  featured?: boolean;
  ctaLabel: string;
}

const CARDS: Card[] = [
  {
    sku: 'monthly',
    title: 'Monthly Membership',
    price: '$500',
    sub: 'per month — 20 tokens included each month',
    features: [
      'Full ConstructiVision Web editor',
      'AI pick simulation and auto-layout',
      '20 included tokens every month',
      'Cancel anytime — keeps tokens forever',
    ],
    ctaLabel: 'Start monthly',
  },
  {
    sku: 'annual',
    title: 'Annual Membership',
    price: '$4,800',
    sub: 'per year — save 20% (vs $6,000 monthly)',
    features: [
      'Everything in Monthly',
      '20 included tokens every month (240 / year)',
      'Best value for active production work',
      'Cancel anytime — keeps tokens forever',
    ],
    featured: true,
    ctaLabel: 'Start annual',
  },
  {
    sku: 'bundle_10',
    title: 'Token 10-pack',
    price: '$500',
    sub: 'one-time — tokens never expire',
    features: [
      'No membership required',
      'Print or export 10 deliverables',
      'Use in ConstructiVision Web or ConstructiVision-CAD',
      'Top up whenever you need more',
    ],
    ctaLabel: 'Buy 10-pack',
  },
  {
    sku: 'bundle_100',
    title: 'Token 100-pack',
    price: '$2,500',
    sub: 'one-time — best per-token value',
    features: [
      'No membership required',
      'Print or export 100 deliverables',
      'Half the per-token cost of the 10-pack',
      'Tokens never expire',
    ],
    ctaLabel: 'Buy 100-pack',
  },
];

const FAQ: { q: string; a: string }[] = [
  {
    q: 'Do tokens expire?',
    a: 'No. Tokens never expire. If you cancel a membership, every unspent token stays in your account.',
  },
  {
    q: 'Can I use tokens in both ConstructiVision Web and ConstructiVision-CAD?',
    a: 'Yes. Tokens live with your organization, not a single app. They\u2019re spent differently in each, though: in Web, one PDF/DXF/CSV/print costs one token; in CAD, one token licenses a drawing once and every later print or export from that drawing is free.',
  },
  {
    q: 'What counts as one token in ConstructiVision Web?',
    a: 'Each PDF panel book, DXF, CSV material list, or printed page is one token. Saving and re-opening your project, sending a .cvpanel to a colleague, or browsing in 3D is free.',
  },
  {
    q: 'What counts as one token in ConstructiVision-CAD?',
    a: 'One token licenses one drawing — for life. The first time you produce a deliverable (PDF, DXF, CSV, or print) from a drawing, one token is spent and the drawing is licensed. Every later print, plot, or export from that same drawing is free, with or without an internet connection. Editing, opening, and reviewing existing panels never costs a token.',
  },
  {
    q: 'What does membership unlock?',
    a: 'Membership unlocks the ConstructiVision Web panel editor, AI pick simulation, and auto-layout. Without it, the web app stays in read-only view.',
  },
  {
    q: 'What happens if I cancel?',
    a: 'You keep editing in the web app through the end of the period you already paid for. After that, the web app drops to read-only and AI features lock — but your tokens are still there for print and export, and every drawing you already licensed in CAD keeps printing forever.',
  },
  {
    q: 'Do I need to be online to use ConstructiVision-CAD?',
    a: 'Only to pair your machine once and to license each new drawing once. Once a drawing has been licensed, it can be opened, plotted, printed, and exported with no internet connection for the life of the file.',
  },
  {
    q: 'Is there a free trial?',
    a: 'Yes — 7 days of full membership plus 10 free tokens when you create an account. No credit card required.',
  },
];

export default function Pricing() {
  const { session, balance, membership } = useAuth();
  const [signinOpen, setSigninOpen] = useState(false);
  const [busy, setBusy] = useState<CheckoutSku | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function onCta(sku: CheckoutSku) {
    if (!session) { setSigninOpen(true); return; }
    try {
      setBusy(sku); setError(null);
      await startCheckout(sku);
      // startCheckout redirects to Stripe; no need to clear busy.
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(null);
    }
  }

  return (
    <div className="pricing-page">
      <a href={import.meta.env.BASE_URL} className="pricing-back">&#8592; ConstructiVision</a>

      <header className="pricing-hero">
        <h1>Pricing built around how shops actually work</h1>
        <p>
          Detailers need the editor and AI. Field crews need to print. We sell the two
          separately so you only pay for what you use.
        </p>
      </header>

      <section className="pricing-axes">
        <div className="pricing-axis">
          <h2>Membership</h2>
          <p>Unlocks the ConstructiVision Web editor and AI features.</p>
          <ul>
            <li>Panel editor with full mutation</li>
            <li>AI pick simulation</li>
            <li>Auto-layout panel generator</li>
            <li>20 tokens included every month</li>
          </ul>
        </div>
        <div className="pricing-axis">
          <h2>Tokens</h2>
          <p>Spent on prints and exports. Same tokens in Web and CAD.</p>
          <ul>
            <li>In CAD: 1 token licenses 1 drawing forever — every later print and export from that drawing is free</li>
            <li>In Web: 1 token = 1 PDF, DXF, CSV, or printed page</li>
            <li>Native .cvpanel / .cvsite transfers are free</li>
            <li>Buy in 10-packs or 100-packs</li>
            <li>Never expire — yours forever</li>
          </ul>
        </div>
      </section>

      <section className="pricing-cards">
        {CARDS.map(c => (
          <div key={c.sku} className={`pricing-card${c.featured ? ' featured' : ''}`}>
            <h3>{c.title}</h3>
            <div className="price">{c.price}</div>
            <div className="price-sub">{c.sub}</div>
            <ul>{c.features.map((f, i) => <li key={i}>{f}</li>)}</ul>
            <button
              className={`cv-btn${c.featured ? ' cv-btn-primary' : ''}`}
              disabled={busy !== null}
              onClick={() => onCta(c.sku)}
            >
              {busy === c.sku ? 'Redirecting…' : c.ctaLabel}
            </button>
          </div>
        ))}
      </section>

      {session && (() => {
        const status = membership?.status ?? null;
        const badgeKind = status === 'active' ? 'active'
          : status === 'trial' ? 'trial'
          : 'inactive';
        const badgeLabel = status === 'active' ? 'Active'
          : status === 'trial' ? 'Trial'
          : status === 'past_due' ? 'Past due'
          : status === 'canceled' ? 'Canceled'
          : status === 'expired' ? 'Expired'
          : 'No membership';
        return (
          <aside className="pricing-account-card" aria-label="Your account">
            <div className="pricing-account-row">
              <span className="pricing-account-label">Signed in as</span>
              <strong className="pricing-account-value">{session.user.email}</strong>
            </div>
            <div className="pricing-account-row">
              <span className="pricing-account-label">Membership</span>
              <span className={`pricing-account-badge pricing-account-badge-${badgeKind}`}>{badgeLabel}</span>
            </div>
            <div className="pricing-account-row">
              <span className="pricing-account-label">Token balance</span>
              <strong className="pricing-account-value">{balance}</strong>
            </div>
          </aside>
        );
      })()}

      {error && <div className="signin-error" role="alert">{error}</div>}

      <section className="pricing-faq">
        <h2>Frequently asked</h2>
        {FAQ.map((f, i) => (
          <div key={i} className="pricing-faq-item">
            <h3>{f.q}</h3>
            <p>{f.a}</p>
          </div>
        ))}
      </section>

      {signinOpen && (
        <div className="cv-modal-backdrop" onClick={() => setSigninOpen(false)}>
          <div className="cv-modal" onClick={e => e.stopPropagation()}>
            <button className="cv-modal-close" onClick={() => setSigninOpen(false)} aria-label="Close">×</button>
            <SignInPanel
              title="Sign in to continue"
              subtitle="Once you're signed in, we'll send you to Stripe to complete the purchase."
            />
          </div>
        </div>
      )}
    </div>
  );
}
